import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { scale } from '@components/frame';
import { SCREEN_BORDER_RADIUS, NOT_PANEL_VIEW_MODE_WIDTH } from '@v2/pages/common/constants/base.constants';
import { useHandleNavigationMachinesHeader } from '@v2/pages/machines/hooks/useHandleNavigationMachineHeader';
import { usePanelViewMode } from '@v2/pages/machines/hooks/usePanelViewMode';
import { useDeviceSize } from '@v2/pages/common/hooks/useDeviceSize';
import { selectSelectedItem } from '@reducers/machines';
import { NCFileGeneralContentProps } from '@v2/pages/machines/modules/now/components/NC-file/components/NCFileGeneralContent';

export interface UseManageNCFileGeneralContentParams extends Pick<NCFileGeneralContentProps, 'maxDetailsWidth' | 'noMachine' | 'ncFileData' | 'ncFileDataLoading' | 'currentNcFileId' | 'ncFileDataLoadingForList'> {}

export const useManageNCFileGeneralContent = ({
  maxDetailsWidth = scale(NOT_PANEL_VIEW_MODE_WIDTH),
  noMachine,
  ncFileData,
  ncFileDataLoading,
  currentNcFileId,
  ncFileDataLoadingForList,
}: UseManageNCFileGeneralContentParams) => {
  const selectedItem = useSelector(selectSelectedItem);
  const { panelViewMode } = usePanelViewMode();
  const { isMachinesScreen } = useHandleNavigationMachinesHeader();
  const { detailsWidth } = useDeviceSize();

  const isNCFile = !!selectedItem?.machineStatus?.ncFile;

  const ncFileStyle = !noMachine ? { height: '100%', flex: 1 } : {};

  const shouldNotShowLoader = useMemo(() => {
    if (isMachinesScreen) return true;
    if (ncFileData && ncFileData?.length !== 1) {
      return !ncFileDataLoadingForList;
    }
    return ncFileData && currentNcFileId && ncFileData[0]?.ncFileId === Number(currentNcFileId);
  }, [isMachinesScreen, ncFileData, currentNcFileId, ncFileDataLoadingForList]);

  const borderRadiusConfig = {
    borderRadius: scale(SCREEN_BORDER_RADIUS),
  };

  const updatedSelectedItem = ncFileData
    ? {
        ...selectedItem,
        machineStatus: {
          ...selectedItem.machineStatus,
          runTimePerPcsSecond: ncFileData[0]?.runTimePerPcsSecond,
          changeTimePerPcsSecond: ncFileData[0]?.changeTimePerPcsSecond,
          setupTimeSecond: ncFileData[0]?.setupTimeSecond,
        },
      }
    : selectedItem;

  const ncFileProps = { selectedItem: updatedSelectedItem, detailsWidth, panelViewMode };

  const shouldDisplaySingleNCFIle = (!noMachine ? isNCFile : ncFileData && ncFileData?.length === 1) && !ncFileDataLoading && shouldNotShowLoader;

  return {
    selectedItem,
    isNCFile,
    ncFileStyle,
    shouldNotShowLoader,
    borderRadiusConfig,
    updatedSelectedItem,
    ncFileProps,
    shouldDisplaySingleNCFIle,
    maxDetailsWidth,
  };
};
